import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminSubscriptions, setSubscriptionStatus } from "@/services/adminService";
import type { AdminSubscription } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminStatCard from "@/pages/portal/admin/components/AdminStatCard";
import {
  adminCard,
  adminTh,
  adminTd,
  adminIconBtn,
  statusBadge,
} from "@/pages/portal/admin/config/ui";

const fmt = (n: number) => n.toLocaleString("en-IN");
const fmtDate = (d: string) => new Date(d).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });

export default function SubscriptionsSection() {
  const { accessToken } = useAuth();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminSubscriptions(accessToken),
    [] as AdminSubscription[],
    [accessToken],
  );

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | AdminSubscription["status"]>("all");
  const [busyId, setBusyId] = useState<number | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((s) => {
      if (status !== "all" && s.status !== status) return false;
      if (!q) return true;
      return [s.businessName, s.ownerName, s.planSlug].join(" ").toLowerCase().includes(q);
    });
  }, [data, search, status]);

  const active   = data.filter((s) => s.status === "active").length;
  const expiring = data.filter((s) => s.status === "expiring").length;
  const revenue  = data.filter((s) => s.status === "active" || s.status === "expiring").reduce((sum, s) => sum + s.amount, 0);

  async function changeStatus(row: AdminSubscription, next: AdminSubscription["status"]) {
    setData((prev) => prev.map((s) => (s.id === row.id ? { ...s, status: next } : s)));
    setBusyId(row.id);
    try {
      await setSubscriptionStatus(accessToken, row.id, next);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <AdminStatCard label="Active Subscriptions" value={loading ? "—" : fmt(active)} icon="ri-bank-card-line" accent="emerald" />
        <AdminStatCard label="Expiring Soon" value={loading ? "—" : fmt(expiring)} icon="ri-alarm-warning-line" accent="amber" sub="within 30 days" />
        <AdminStatCard label="Annual Recurring Revenue" value={loading ? "—" : `₹${fmt(revenue)}`} icon="ri-money-rupee-circle-line" accent="primary" />
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search subscriptions…" />
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="h-9 cursor-pointer rounded-lg border border-background-300 bg-background-100 px-3 text-xs text-foreground-700 outline-none focus:border-primary-400">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="expiring">Expiring</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <span className="ml-auto text-xs text-foreground-500">{loading ? "Loading…" : `${filtered.length} of ${data.length}`}</span>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-bank-card-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No subscriptions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>Business</th>
                  <th className={adminTh}>Owner</th>
                  <th className={adminTh}>Plan</th>
                  <th className={adminTh}>Amount</th>
                  <th className={adminTh}>Started</th>
                  <th className={adminTh}>Expires</th>
                  <th className={adminTh}>Status</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-background-100/40">
                    <td className={`${adminTd} font-semibold text-foreground-900`}>{s.businessName}</td>
                    <td className={adminTd}>{s.ownerName}</td>
                    <td className={adminTd}>
                      <span className="rounded-full bg-background-100 px-2 py-0.5 text-[10px] font-bold capitalize text-foreground-600">{s.planSlug}</span>
                      <span className="ml-1.5 text-[11px] text-foreground-400">{s.billingCycle}</span>
                    </td>
                    <td className={`${adminTd} tabular-nums`}>{s.amount > 0 ? `₹${fmt(s.amount)}` : "Free"}</td>
                    <td className={`${adminTd} text-xs text-foreground-500`}>{fmtDate(s.startedAt)}</td>
                    <td className={`${adminTd} text-xs text-foreground-500`}>{fmtDate(s.expiresAt)}</td>
                    <td className={adminTd}>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${statusBadge(s.status)}`}>{s.status}</span>
                    </td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        {s.status !== "cancelled" && s.status !== "expired" && (
                          <button type="button" disabled={busyId === s.id} onClick={() => changeStatus(s, "cancelled")} className={adminIconBtn} title="Cancel subscription">
                            <i className="ri-close-circle-line text-base text-rose-600" />
                          </button>
                        )}
                        {(s.status === "cancelled" || s.status === "expired") && (
                          <button type="button" disabled={busyId === s.id} onClick={() => changeStatus(s, "active")} className={adminIconBtn} title="Reactivate subscription">
                            <i className="ri-refresh-line text-base text-emerald-600" />
                          </button>
                        )}
                        {s.status === "expiring" && (
                          <button type="button" disabled={busyId === s.id} onClick={() => changeStatus(s, "active")} className={adminIconBtn} title="Renew for another year">
                            <i className="ri-calendar-check-line text-base text-emerald-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}